import { BrowserProvider } from 'ethers';
import { Connection, PublicKey } from '@solana/web3.js';
import nacl from 'tweetnacl';
import bs58 from 'bs58';

export const web3Service = {
  provider: null,
  signer: null,
  solanaConnection: null,

  // Ethereum wallet methods
  async connectMetaMask() {
    try {
      if (!window?.ethereum) {
        throw new Error('MetaMask not installed');
      }

      const accounts = await window?.ethereum?.request({ 
        method: 'eth_requestAccounts' 
      });

      if (!accounts || accounts?.length === 0) {
        throw new Error('No accounts found');
      }

      this.provider = new BrowserProvider(window?.ethereum);
      this.signer = await this.provider?.getSigner();
      const address = await this.signer?.getAddress();
      const network = await this.provider?.getNetwork();

      return {
        address,
        chainId: Number(network?.chainId),
        provider: 'MetaMask',
        walletType: 'ethereum',
        error: null
      };
    } catch (error) {
      return {
        address: null,
        chainId: null,
        provider: null,
        walletType: null,
        error: { message: error?.message || 'Failed to connect MetaMask' }
      };
    }
  },

  // Solana wallet methods
  async connectSolanaWallet() {
    try {
      // Check for Phantom wallet
      const provider = window?.solana || window?.phantom?.solana;
      
      if (!provider) {
        throw new Error('Solana wallet not installed. Please install Phantom wallet.');
      }

      if (!provider?.isPhantom) {
        throw new Error('Please use Phantom wallet for Solana connection');
      }

      const response = await provider?.connect();
      const publicKey = response?.publicKey?.toString();

      // Initialize Solana connection (mainnet-beta)
      this.solanaConnection = new Connection('https://api.mainnet-beta.solana.com');

      return {
        address: publicKey,
        provider: 'Phantom',
        walletType: 'solana',
        error: null
      };
    } catch (error) {
      return {
        address: null,
        provider: null,
        walletType: null,
        error: { message: error?.message || 'Failed to connect Solana wallet' }
      };
    }
  },

  // Sign message for Ethereum authentication
  async signMessageEthereum(message) {
    try {
      if (!this.signer) {
        throw new Error('No Ethereum wallet connected');
      }

      const signature = await this.signer?.signMessage(message);
      return { signature, error: null };
    } catch (error) {
      return {
        signature: null,
        error: { message: error?.message || 'Failed to sign message' }
      };
    }
  },

  // Sign message for Solana authentication
  async signMessageSolana(message) {
    try {
      const provider = window?.solana || window?.phantom?.solana;
      
      if (!provider) {
        throw new Error('No Solana wallet connected');
      }

      const encodedMessage = new TextEncoder()?.encode(message);
      const signedMessage = await provider?.signMessage(encodedMessage, 'utf8');
      
      return { 
        signature: bs58?.encode(signedMessage?.signature),
        publicKey: signedMessage?.publicKey?.toString(),
        error: null 
      };
    } catch (error) {
      return {
        signature: null,
        publicKey: null,
        error: { message: error?.message || 'Failed to sign message' }
      };
    }
  },

  // Verify Solana signature
  verifySolanaSignature(message, signature, publicKey) {
    try {
      const encodedMessage = new TextEncoder()?.encode(message);
      const signatureBytes = bs58?.decode(signature);
      const publicKeyBytes = new PublicKey(publicKey)?.toBytes();
      
      return nacl?.sign?.detached?.verify(encodedMessage, signatureBytes, publicKeyBytes);
    } catch (error) {
      console.error('Signature verification failed:', error);
      return false;
    }
  },

  async getBalance(address) {
    try {
      if (!this.provider) {
        throw new Error('No provider connected');
      }

      const balance = await this.provider?.getBalance(address);
      const ethBalance = Number(balance) / 1e18;

      return {
        balance: ethBalance,
        formatted: ethBalance?.toFixed(4),
        error: null
      };
    } catch (error) {
      return {
        balance: null,
        formatted: null,
        error: { message: error?.message || 'Failed to get balance' }
      };
    }
  },

  async getSolanaBalance(address) {
    try {
      if (!this.solanaConnection) {
        this.solanaConnection = new Connection('https://api.mainnet-beta.solana.com');
      }

      const publicKey = new PublicKey(address);
      const balance = await this.solanaConnection?.getBalance(publicKey);
      const solBalance = balance / 1e9;

      return {
        balance: solBalance,
        formatted: solBalance?.toFixed(4),
        error: null
      };
    } catch (error) {
      return {
        balance: null,
        formatted: null,
        error: { message: error?.message || 'Failed to get Solana balance' }
      };
    }
  },

  async getTransactionHistory(address, limit = 10) {
    try {
      if (!this.provider) {
        throw new Error('No provider connected');
      }

      const currentBlock = await this.provider?.getBlockNumber();
      const transactions = [];

      // Note: For production, use an indexer service like Etherscan API or The Graph
      // This is a simplified implementation
      for (let i = 0; i < Math.min(limit, 5); i++) {
        const blockNumber = currentBlock - i;
        const block = await this.provider?.getBlock(blockNumber);
        
        if (block?.transactions?.length > 0) {
          for (const txHash of block?.transactions?.slice(0, 3)) {
            const tx = await this.provider?.getTransaction(txHash);
            if (tx?.from?.toLowerCase() === address?.toLowerCase() || 
                tx?.to?.toLowerCase() === address?.toLowerCase()) {
              transactions?.push({
                hash: tx?.hash,
                from: tx?.from,
                to: tx?.to,
                value: Number(tx?.value) / 1e18,
                timestamp: block?.timestamp,
                blockNumber: tx?.blockNumber
              });
            }
          }
        }
        
        if (transactions?.length >= limit) break;
      }

      return {
        transactions,
        error: null
      };
    } catch (error) {
      return {
        transactions: [],
        error: { message: error?.message || 'Failed to get transaction history' }
      };
    }
  },

  async switchNetwork(chainId) {
    try {
      if (!window?.ethereum) {
        throw new Error('No wallet connected');
      }

      await window?.ethereum?.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: `0x${chainId?.toString(16)}` }]
      });

      return { success: true, error: null };
    } catch (error) {
      // If network doesn't exist, add it
      if (error?.code === 4902) {
        return { 
          success: false, 
          error: { message: 'Network not found. Please add it manually.' } 
        };
      }
      return {
        success: false,
        error: { message: error?.message || 'Failed to switch network' }
      };
    }
  },

  async disconnect() {
    try {
      // Disconnect Ethereum
      this.provider = null;
      this.signer = null;
      
      // Disconnect Solana
      const solanaProvider = window?.solana || window?.phantom?.solana;
      if (solanaProvider) {
        await solanaProvider?.disconnect();
      }
      this.solanaConnection = null;
      
      return { success: true, error: null };
    } catch (error) {
      return {
        success: false,
        error: { message: error?.message || 'Failed to disconnect' }
      };
    }
  },

  onAccountsChanged(callback) {
    if (window?.ethereum) {
      window.ethereum?.on('accountsChanged', callback);
    }
  },

  onChainChanged(callback) {
    if (window?.ethereum) {
      window.ethereum?.on('chainChanged', callback);
    }
  },

  removeListeners() {
    if (window?.ethereum) {
      window.ethereum?.removeAllListeners('accountsChanged');
      window.ethereum?.removeAllListeners('chainChanged');
    }
  }
};

export default web3Service;